import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  ExternalLink,
  CreditCard,
  Activity,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Upload,
  Palette,
  Globe
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [tierFilter, setTierFilter] = useState('ALL');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showModal, setShowModal] = useState(null); // 'create', 'edit', 'credits', 'activity'

  useEffect(() => {
    fetchTenants();
  }, [statusFilter, tierFilter, search]);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (tierFilter !== 'ALL') params.append('tier', tierFilter);
      if (search) params.append('search', search);

      const response = await axios.get(`${API_BASE_URL}/tenants?${params.toString()}`);
      setTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = () => {
    setSelectedTenant(null);
    setShowModal('create');
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setShowModal('edit');
  };

  const handleAdjustCredits = (tenant) => {
    setSelectedTenant(tenant);
    setShowModal('credits');
  };

  const handleViewActivity = (tenant) => {
    setSelectedTenant(tenant);
    setShowModal('activity');
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      SUSPENDED: 'bg-red-100 text-red-800',
      TRIAL: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getTierBadge = (tier) => {
    const badges = {
      PREMIUM: 'bg-blue-100 text-blue-800',
      ENTERPRISE: 'bg-purple-100 text-purple-800',
      TRIAL: 'bg-gray-100 text-gray-800'
    };
    return badges[tier] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <Building2 size={32} className="text-blue-600" />
                Tenant Management
              </h1>
              <p className="text-gray-600 mt-2">Manage white-labeled college sub-domains</p>
            </div>
            <button
              onClick={handleCreateTenant}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <Plus size={20} />
              Create New Tenant
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tenants..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="TRIAL">Trial</option>
              </select>
            </div>

            {/* Tier Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <select
                value={tierFilter}
                onChange={(e) => setTierFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="ALL">All Tiers</option>
                <option value="PREMIUM">Premium</option>
                <option value="ENTERPRISE">Enterprise</option>
                <option value="TRIAL">Trial</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <div className="text-sm text-gray-600 mb-1">Total Tenants</div>
            <div className="text-3xl font-bold text-gray-800">{tenants.length}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <div className="text-sm text-gray-600 mb-1">Active</div>
            <div className="text-3xl font-bold text-gray-800">
              {tenants.filter(t => t.status === 'ACTIVE').length}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
            <div className="text-sm text-gray-600 mb-1">Total Students</div>
            <div className="text-3xl font-bold text-gray-800">
              {tenants.reduce((sum, t) => sum + (parseInt(t.total_students) || 0), 0)}
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
            <div className="text-sm text-gray-600 mb-1">Premium 100</div>
            <div className="text-3xl font-bold text-gray-800">
              {tenants.filter(t => t.tier === 'PREMIUM').length} / 100
            </div>
          </div>
        </div>

        {/* Tenants Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Subdomain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Students
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Credits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tenants.length > 0 ? (
                  tenants.map(tenant => (
                    <tr key={tenant.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {tenant.logo_url ? (
                            <img
                              src={tenant.logo_url}
                              alt={tenant.college_name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center">
                              <Building2 size={20} className="text-gray-500" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-800">{tenant.college_name}</div>
                            <div className="text-sm text-gray-500">{tenant.college_full_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <code className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {tenant.tenant_slug}
                          </code>
                          <a
                            href={`https://${tenant.tenant_slug}.skillgen.com`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-blue-600"
                          >
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(tenant.status)}`}>
                          {tenant.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getTierBadge(tenant.tier)}`}>
                          {tenant.tier}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-800 font-medium">{tenant.total_students || 0}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-800 font-medium">{tenant.available_credits || 0}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditTenant(tenant)}
                            className="p-2 hover:bg-blue-100 rounded transition-colors"
                            title="Edit Tenant"
                          >
                            <Edit2 size={16} className="text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleAdjustCredits(tenant)}
                            className="p-2 hover:bg-green-100 rounded transition-colors"
                            title="Adjust Credits"
                          >
                            <CreditCard size={16} className="text-green-600" />
                          </button>
                          <button
                            onClick={() => handleViewActivity(tenant)}
                            className="p-2 hover:bg-purple-100 rounded transition-colors"
                            title="View Activity"
                          >
                            <Activity size={16} className="text-purple-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No tenants found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showModal === 'create' && <CreateTenantModal onClose={() => setShowModal(null)} onSuccess={fetchTenants} />}
      {showModal === 'edit' && selectedTenant && (
        <EditTenantModal tenant={selectedTenant} onClose={() => setShowModal(null)} onSuccess={fetchTenants} />
      )}
      {showModal === 'credits' && selectedTenant && (
        <CreditAdjustmentModal tenant={selectedTenant} onClose={() => setShowModal(null)} onSuccess={fetchTenants} />
      )}
      {showModal === 'activity' && selectedTenant && (
        <ActivityLogModal tenant={selectedTenant} onClose={() => setShowModal(null)} />
      )}
    </div>
  );
};

// Create Tenant Modal Component (stub - full implementation would be similar)
const CreateTenantModal = ({ onClose, onSuccess }) => {
  // Implementation similar to student modals
  return <div>Create Tenant Modal</div>;
};

const EditTenantModal = ({ tenant, onClose, onSuccess }) => {
  return <div>Edit Tenant Modal</div>;
};

const CreditAdjustmentModal = ({ tenant, onClose, onSuccess }) => {
  return <div>Credit Adjustment Modal</div>;
};

const ActivityLogModal = ({ tenant, onClose }) => {
  return <div>Activity Log Modal</div>;
};

export default TenantManagement;
